import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

export type BundleComponent = components["schemas"]["ListingComponent"];

/**
 * Query options that resolve a bundle/pack listing code to its base components
 * (base SKU × multiplicity), so the order-detail items table can expand a pack
 * line to its base rows — the way sino-legacy does (`340905_P1x12 → 340905 ×12`).
 *
 * Returned as options (not a `useQuery` call) so the items table can drive a
 * variable-length `useQueries` over every bundle row without breaking the
 * rules-of-hooks. A line that is not a listing (single base SKU) 404s and
 * yields `[]` — expansion is purely additive and never blocks the page.
 *
 * Mirrors `promotion/use-gift-expansion` deliberately: features are isolated
 * (CLAUDE.md rule 2), so orders owns its own copy rather than importing it.
 */
export function bundleExpansionQuery(code: string) {
  return {
    queryKey: ["orders", "bundle-expansion", code] as const,
    queryFn: async (): Promise<BundleComponent[]> => {
      const { data, error } = await apiClient.GET("/v1/listings/{code}", {
        params: { path: { code } },
      });
      if (error) return []; // 404 / not a listing → single base SKU, no expansion
      return data?.components ?? [];
    },
    staleTime: 30_000,
  };
}
